var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { RG_TYPE } from "@project-rouge/rg-core";
import { NmcsState } from "./index134.js";
import { getCurrentLength } from "./index214.js";
import { getDoorDistanceFromRight } from "./index211.js";
import { getEntranceCount } from "./index215.js";
import { getLiftCoreCount } from "./index216.js";
import { getStairCoreCount } from "./index217.js";
import { getFireCoreCount } from "./index218.js";
import { getStartClashStatus } from "./index212.js";
class AggNmcsState extends NmcsState {
  constructor({ gap, sequence = [], totalLength, corridorLengths = [0], requiredShift = 0, data }) {
    super();
    __publicField(this, "sequence", []);
    __publicField(this, "gap");
    __publicField(this, "data");
    __publicField(this, "_requiredShift", 0);
    __publicField(this, "_totalLength");
    __publicField(this, "_corridorLengths");
    __publicField(this, "_entranceCount");
    __publicField(this, "_fireCoreCount");
    __publicField(this, "_liftCoreCount");
    __publicField(this, "_stairCoreCount");
    __publicField(this, "update", (action) => {
      this.sequence.push(action);
      if (this._totalLength === 0) {
        this._totalLength += action.unit.size[0];
        if (this.data.lockEnds[0] === false) {
          const startClosingDistance = this.data.endClosingDistances[0];
          const startEntranceDistance = this.data.endEntranceDistances[0];
          const clashStatus = getStartClashStatus({ action, startClosingDistance, startEntranceDistance });
          if (!clashStatus) {
            this._requiredShift = startClosingDistance - (action.doorPosition - 0.5 * action.doorWidth);
          }
        }
      } else {
        this._totalLength += this.gap + action.unit.size[0];
      }
      this.updateCorridorDistance(action, this._requiredShift);
      if (action.unit.type === RG_TYPE.Entrance) {
        this._entranceCount++;
      }
      if (action.unit.type === RG_TYPE.Core && action.unit.data.hasFireLift) {
        this._fireCoreCount++;
      }
      if (action.unit.type === RG_TYPE.Core && action.unit.data.hasNonFireLift) {
        this._liftCoreCount++;
      }
      if (action.unit.type === RG_TYPE.Core && !action.unit.data.hasFireLift && !action.unit.data.hasNonFireLift) {
        this._stairCoreCount++;
      }
    });
    __publicField(this, "clone", () => {
      return new AggNmcsState({
        sequence: this.sequence.slice(),
        totalLength: this._totalLength,
        corridorLengths: this._corridorLengths.slice(),
        gap: this.gap,
        requiredShift: this._requiredShift,
        data: this.data
      });
    });
    this.gap = gap;
    this.data = data;
    this.sequence = sequence;
    this._totalLength = totalLength ?? getCurrentLength(this.sequence, this.gap);
    this._corridorLengths = corridorLengths;
    this._entranceCount = getEntranceCount(this.sequence);
    this._fireCoreCount = getFireCoreCount(this.sequence);
    this._liftCoreCount = getLiftCoreCount(this.sequence);
    this._stairCoreCount = getStairCoreCount(this.sequence);
    this._requiredShift = requiredShift;
  }
  get requiredShift() {
    return this._requiredShift;
  }
  get totalLength() {
    return this._totalLength;
  }
  get corridorLengths() {
    return this._corridorLengths;
  }
  get entranceCount() {
    return this._entranceCount;
  }
  get fireCoreCount() {
    return this._fireCoreCount;
  }
  get liftCoreCount() {
    return this._liftCoreCount;
  }
  get stairCoreCount() {
    return this._stairCoreCount;
  }
  /** this function updates the array of travel distances
   * travel distances are an array where each item in the array
   * represents a travel distance between cores.
   * Where there are no cores at ends:
   * At the start the distance to the first door must be considered.
   * At the end the distance to the last door must be considered, this is not
   * considered here as it is not possible to know if the unit is the last.
   * Therefore this detail must be considered elsewhere.
   */
  updateCorridorDistance(action, shift = 0) {
    if (action.unit.type === RG_TYPE.Core) {
      this._corridorLengths.push(0);
    } else if (this.sequence.length === 1) {
      if (this.data.hasCornerAt[0] === false) {
        this._corridorLengths[0] += Math.max(getDoorDistanceFromRight(action), shift + action.unit.size[0] - this.data.endEntranceDistances[0]);
      } else {
        this._corridorLengths[this._corridorLengths.length - 1] += action.unit.size[0] + this.data.distToCore[0];
      }
    } else {
      this._corridorLengths[this._corridorLengths.length - 1] += action.unit.size[0];
    }
  }
  finalUpdate(data) {
    this.finishCorridorDistance(data);
  }
  /** this should be called when a aggregation has finished
   * it accounts for the door position of the final apartment
   * as well as the final door position on the opposite muscle
   * as long as there is no core at the end of the muscle.
   */
  finishCorridorDistance(rulesData) {
    const firstAction = this.sequence[0];
    if (!firstAction)
      return;
    const shift = rulesData.targetLength - this._totalLength;
    const isShiftedToRight = !rulesData.lockEnds[0] && rulesData.lockEnds[1];
    if (isShiftedToRight && getDoorDistanceFromRight(firstAction) < firstAction.unit.size[0] - this.data.endEntranceDistances[0] + shift) {
      this._corridorLengths[0] += shift - this._requiredShift;
    }
    if (this.data.hasCornerAt[1])
      return;
    const lastUnit = this.sequence[this.sequence.length - 1];
    if (!lastUnit && this._corridorLengths[this._corridorLengths.length - 1] !== 0)
      return;
    const startOfDoorPosFromEnd = getDoorDistanceFromRight(lastUnit) + lastUnit.doorWidth;
    let oppositeDoorPosFromEnd = this.data.endEntranceDistances[1];
    if (!isShiftedToRight) {
      oppositeDoorPosFromEnd -= shift - this._requiredShift;
    }
    const endDoorPos = Math.min(startOfDoorPosFromEnd, oppositeDoorPosFromEnd);
    this._corridorLengths[this._corridorLengths.length - 1] -= endDoorPos;
  }
}
export {
  AggNmcsState
};
//# sourceMappingURL=index131.js.map
